import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { gosafeApi } from "../../api";
import {
  loadingDoctorsSelect,
  loadedDoctorsSelect,
  failedDoctorsSelect,
  loading,
  errorMessage,
  successMessage,
  loadingWorkShifts,
  loadedWorkShifts,
  failedWorkShifts,
  addWorkShift,
  changeParams,
  resetParams,
  loaded,
  changePageSize,
  changePage,
} from "../../store/secretary/work_shift";
import { translations, replaceEmptyToNull } from "../../helpers";

export const useWorkShiftStore = () => {
  const dispatch = useDispatch();

  const {
    doctorsSelect,
    shiftTypes,
    message,
    isLoading,
    workShifts,
    params,
    pageSize,
    page,
    totalPages,
  } = useSelector((state) => state.workShift);

  const getDoctorsBySelect = useCallback(async () => {
    try {
      dispatch(loadingDoctorsSelect());

      const { data } = await gosafeApi.get("doctor/bySelect");

      dispatch(loadedDoctorsSelect(data));
      // dispatch(failedDoctorsSelect("Hola"));
      return;
    } catch (error) {
      dispatch(
        failedDoctorsSelect(
          translations[error?.response?.data?.message] ||
            error?.response?.data?.message ||
            error?.message
        )
      );
      return;
    }
  }, [dispatch]);

  const startAddWorkShift = useCallback(
    async (body) => {
      try {
        dispatch(loading());

        const newBody = {
          ...body,
          date: `${body.date}T05:00:00.000Z`,
        };

        // console.log("body", newBody);

        const { data } = await gosafeApi.post("workShift", newBody);

        dispatch(addWorkShift(data.workShift));
        dispatch(successMessage(translations[data.message] || data.message));
        return;
      } catch (error) {
        dispatch(
          errorMessage(
            translations[error?.response?.data?.message] ||
              error?.response?.data?.message ||
              error?.message
          )
        );
      }
    },
    [dispatch]
  );

  const startGetWorkShifts = useCallback(
    async (params, page, pageSize) => {
      try {
        dispatch(loadingWorkShifts());

        const filteredParams = Object.fromEntries(
          Object.entries({
            ...params,
            startDate: params.startDate
              ? `${params.startDate}T00:00:00.000Z`
              : "",
            endDate: params.endDate ? `${params.endDate}T23:59:59.000Z` : "",
          }).filter(([_, value]) => value !== "")
        );

        // console.log("params", params);
        // console.log("filteredParams", filteredParams);

        const { data } = await gosafeApi.get("workShift", {
          params: {
            ...filteredParams,
            page,
            pageSize,
          },
        });

        dispatch(loadedWorkShifts(data));
        return;
      } catch (error) {
        dispatch(
          failedWorkShifts(
            translations[error?.response?.data?.message] ||
              error?.response?.data?.message ||
              error?.message
          )
        );
        return;
      }
    },
    [dispatch]
  );

  const startWorkShiftFilter = useCallback(
    async (params) => {
      try {
        dispatch(changeParams(params));

        const { startDate, endDate } = params;

        if ((startDate && !endDate) || (!startDate && endDate)) {
          throw new Error(
            "Si proporciona una fecha inicial, también debe proporcionar una fecha final, y viceversa."
          );
        }

        if (startDate && endDate && new Date(endDate) < new Date(startDate)) {
          throw new Error(
            "La fecha final no puede ser menor que la fecha inicial."
          );
        }

        // dispatch(loading());

        await startGetWorkShifts(params, page, pageSize);
        // dispatch(loaded());
        return;
      } catch (error) {
        dispatch(
          failedWorkShifts(
            translations[error?.response?.data?.message] ||
              error?.response?.data?.message ||
              error?.message
          )
        );
        return;
      }
    },
    [dispatch, startGetWorkShifts, page, pageSize]
  );

  const startResetParams = useCallback(async () => {
    try {
      dispatch(resetParams());
      await startGetWorkShifts({}, page, pageSize);
      return;
    } catch (error) {
      dispatch(
        errorMessage(
          translations[error?.response?.data?.message] ||
            error?.response?.data?.message ||
            error?.message
        )
      );
      return;
    }
  }, [dispatch, startGetWorkShifts, page, pageSize]);

  const startChangePageSize = useCallback(
    async (pageSize) => {
      dispatch(changePageSize(pageSize));
      await startGetWorkShifts(params, page, pageSize);
    },
    [dispatch, startGetWorkShifts, params, page]
  );

  const startChangePage = useCallback(
    async (page) => {
      dispatch(changePage(page));
      await startGetWorkShifts(params, page, pageSize);
    },
    [dispatch, startGetWorkShifts, params, pageSize]
  );

  const startAddPage = useCallback(async () => {
    if (totalPages > parseInt(page)) {
      const newPage = (parseInt(page) + 1).toString();
      dispatch(changePage(newPage));
      await startGetWorkShifts(params, newPage, pageSize);
    }
  }, [dispatch, startGetWorkShifts, params, page, pageSize, totalPages]);

  const startRemovePage = useCallback(async () => {
    if (parseInt(page) > 1) {
      const newPage = (parseInt(page) - 1).toString();
      dispatch(changePage(newPage));
      await startGetWorkShifts(params, newPage, pageSize);
    }
  }, [dispatch, startGetWorkShifts, params, page, pageSize, totalPages]);

  const reloadWorkShifts = useCallback(async () => {
    await startGetWorkShifts(params, page, pageSize);
  }, [startGetWorkShifts, params, page, pageSize]);

  return {
    // Props
    doctorsSelect,
    shiftTypes,
    message,
    isLoading,
    workShifts,
    params,
    pageSize,
    page,
    totalPages,

    // Methods
    getDoctorsBySelect,
    startAddWorkShift,
    startGetWorkShifts,
    startWorkShiftFilter,
    startResetParams,
    startChangePageSize,
    startChangePage,
    startAddPage,
    startRemovePage,
    reloadWorkShifts,
  };
};
